import React, {
  forwardRef,
  PropsWithoutRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Container,
  Dropdown,
  Option,
  Options,
  StyledChevron,
  StyledSelectButtonClear,
} from "./Select.styled";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { InputWithValidation } from "../Input/InputWithValidation";
import { IconTriangle } from "../../assets/icons/IconTriangle";
import { Icon } from "../Icon/Icon";

export interface SelectProps {
  name?: string;
  id?: string;
  value?: string;
  label?: string;
  options?: { value: string; label: string }[];
  onChange?: (value?: string) => void;
  onBlur: (id: string, value: string) => void;
  required?: boolean;
  error?: string;
  isDisabled?: boolean;
  schema?: any;
  placeholder?: string;
}

export const Select = forwardRef<
  HTMLInputElement,
  PropsWithoutRef<SelectProps>
>((props, ref) => {
  const {
    onBlur,
    onChange,
    id,
    value,
    options,
    label,
    name,
    required = false,
    error,
    isDisabled,
    schema,
    placeholder,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [cursor, setCursor] = useState(-1);
  let isShowingDisable = false;

  const hideDropdown = () => {
    setIsDropdownVisible(false);
  };

  const showDropdown = () => {
    if (!isShowingDisable) {
      setIsDropdownVisible(true);
    }
    isShowingDisable = false;
  };

  const outsideAction = () => {
    hideDropdown();
    setInputValue("");
    if (!value && isDropdownVisible) {
      onBlur(id || "", value || "");
    }
  };

  useOutsideClick(containerRef, outsideAction);

  const valueOption = useMemo(
    () => options?.find((option) => option.value === value),
    [options, value]
  );

  const filteredOptions = useMemo(
    () =>
      options?.filter(({ label }: any) =>
        label
          .replaceAll(" ", "")
          .toLowerCase()
          .includes(inputValue.toLowerCase().replaceAll(" ", ""))
      ),
    [options, inputValue]
  );

  const handleKeydown = (event: any) => {
    if (event.keyCode === 38 && cursor > 0) {
      setCursor((prevState) => prevState - 1);
      if (dropdownRef.current) dropdownRef.current.focus();
    } else if (
      event.keyCode === 40 &&
      Array.isArray(filteredOptions) &&
      cursor < filteredOptions?.length - 1
    ) {
      setCursor((prevState) => prevState + 1);
      if (dropdownRef.current) dropdownRef.current.focus();
    } else if (event.key.toLowerCase() === "enter") {
      if (filteredOptions && onChange) {
        onChange(filteredOptions[cursor].value);
      }
    }
  };

  return (
    <Container
      ref={containerRef}
      onKeyDown={handleKeydown}
      isDisabled={Boolean(isDisabled)}
    >
      <InputWithValidation
        // id={id}
        value={inputValue}
        onChange={(e) => setInputValue(e)}
        placeholder={valueOption?.label || placeholder}
        label={label}
        error={error}
        name={name ?? ""}
        onFocus={showDropdown}
        // autoComplete="off"
        iconEnd={
          <StyledChevron
            rotated={isDropdownVisible}
            onClick={isDropdownVisible ? hideDropdown : showDropdown}
          >
            <IconTriangle />
          </StyledChevron>
        }
      />

      {value && (
        <StyledSelectButtonClear
          type="button"
          onClick={() => onChange && onChange("")}
        >
          <Icon name="cross" size={10} color="primary" />
        </StyledSelectButtonClear>
      )}

      <Dropdown
        style={{
          borderRadius: "5px",
          overflow: "hidden",
          boxShadow: "box-shadow: rgb(0 0 0 / 15%) 0px 0px 40px;",
          zIndex: "1000",
        }}
        isDropdownVisible={isDropdownVisible}
      >
        {!!filteredOptions?.length && (
          <Options ref={dropdownRef} tabIndex={0}>
            {filteredOptions?.map((option: any, idx: any) => {
              return (
                <Option
                  type="button"
                  key={option.value}
                  onClick={() => {
                    onChange?.(option.value);
                    hideDropdown();
                    setInputValue("");
                    if (typeof schema === 'object' && typeof schema.properties === 'object') {
                        const selectId = id?.replace("root_", "");
                        const nextElementId =
                        Object.keys(schema?.properties)?.findIndex(
                            (id) => id === selectId
                        ) + 1;

                        setTimeout(() => {
                        document
                            ?.getElementById(
                            `root_${
                                Object.keys(schema?.properties)[nextElementId]
                            }`
                            )
                            ?.focus();
                        }, 0);
                    }
                  }}
                  isHover={idx === cursor}
                  isSelected={option.value === value}
                >
                  {option.label}
                </Option>
              );
            })}
          </Options>
        )}
      </Dropdown>
    </Container>
  );
});
